import { Grid, IconButton, Typography } from "@mui/material";
import React from "react";
import { motion } from "framer-motion";
import { ReactComponent as LinkedIcon } from "../linked.svg";
import { ReactComponent as InstaIcon } from "../inst.svg";
import { ReactComponent as MailIcon } from "../mail.svg";
import { ReactComponent as PhoneIcon } from "../phone.svg";

const NAV_LINKS = [
  { label: "About", id: "about" },
  { label: "Experience", id: "experience" },
  { label: "Projects", id: "projects" },
  { label: "Skills", id: "skill" },
  { label: "Education", id: "education" },
];

const SOCIAL_LINKS = [
  {
    Icon: LinkedIcon,
    href: "https://www.linkedin.com/in/utsavoo7/",
    label: "LinkedIn",
    color: "#0077B5",
  },
  {
    Icon: InstaIcon,
    href: "https://www.instagram.com/_.utsav._/",
    label: "Instagram",
    color: "#E1306C",
  },
  {
    Icon: MailIcon,
    href: "mailto:patel.utsav@icloud.com",
    label: "Email",
    color: "#159895",
  },
  {
    Icon: PhoneIcon,
    href: "tel:+917874742111",
    label: "Phone",
    color: "#00C9A7",
  },
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Footer = () => {
  return (
    <footer style={{ position: "relative", overflow: "hidden" }}>
      {/* Top accent line */}
      <div
        style={{
          height: 1,
          background: "linear-gradient(90deg, transparent, #15989566, #00C9A766, transparent)",
        }}
      />

      {/* Subtle background glow */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -40%)",
          width: "60vw",
          height: "60vw",
          background: "radial-gradient(circle, rgba(21,152,149,0.05) 0%, transparent 65%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Grid container px={{ xs: 3, md: 8 }} py={8} spacing={5} style={{ position: "relative" }}>
        {/* ── Col 1: Brand + tagline ───────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <motion.div
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <Typography
              fontFamily="Pacifico"
              fontSize={28}
              sx={{ mb: 1.5, cursor: "pointer" }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Resume.
              <span style={{ color: "#159895" }}>io</span>
            </Typography>
            <Typography
              fontFamily="Poppins"
              fontSize={13}
              sx={{ opacity: 0.5, lineHeight: 1.8, maxWidth: 260, mb: 3 }}
            >
              Building digital experiences that live at the intersection of design and engineering.
            </Typography>

            {/* Social icons */}
            <div style={{ display: "flex", gap: 8 }}>
              {SOCIAL_LINKS.map(({ Icon, href, label, color }) => (
                <motion.div
                  key={label}
                  whileHover={{ y: -4, scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  <IconButton
                    component="a"
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                    aria-label={label}
                    size="small"
                    sx={{
                      border: `1px solid ${color}33`,
                      width: 38,
                      height: 38,
                      "&:hover": {
                        borderColor: color,
                        boxShadow: `0 0 18px ${color}44`,
                        background: `${color}11`,
                      },
                      transition: "all 0.25s ease",
                    }}
                  >
                    <Icon width={16} height={16} />
                  </IconButton>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Grid>

        {/* ── Col 2: Quick nav ─────────────────────────────────────────────── */}
        <Grid item xs={6} md={4}>
          <motion.div
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <Typography
              fontFamily="Poppins"
              fontSize={10}
              fontWeight={600}
              sx={{
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                opacity: 0.4,
                mb: 2.5,
              }}
            >
              Navigation
            </Typography>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {NAV_LINKS.map(({ label, id }) => (
                <motion.div
                  key={id}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
                  onClick={() => scrollTo(id)}
                >
                  <div
                    style={{
                      width: 16,
                      height: 1.5,
                      background: "linear-gradient(90deg, #159895, #00C9A7)",
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    fontFamily="Poppins"
                    fontSize={13}
                    sx={{ opacity: 0.6, "&:hover": { opacity: 1, color: "#159895" }, transition: "all 0.2s" }}
                  >
                    {label}
                  </Typography>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Grid>

        {/* ── Col 3: Contact ───────────────────────────────────────────────── */}
        <Grid item xs={6} md={4}>
          <motion.div
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
          >
            <Typography
              fontFamily="Poppins"
              fontSize={10}
              fontWeight={600}
              sx={{
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                opacity: 0.4,
                mb: 2.5,
              }}
            >
              Contact
            </Typography>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                { Icon: MailIcon, text: "patel.utsav@icloud.com", href: "mailto:patel.utsav@icloud.com" },
                { Icon: PhoneIcon, text: "+91 78747 42111", href: "tel:+917874742111" },
                { Icon: LinkedIcon, text: "linkedin.com/in/utsavoo7", href: "https://www.linkedin.com/in/utsavoo7/", external: true },
              ].map(({ Icon, text, href, external }) => (
                <a
                  key={text}
                  href={href}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 10 }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    border: "1px solid rgba(21,152,149,0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Icon width={14} height={14} />
                  </div>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={12}
                    sx={{ opacity: 0.55, "&:hover": { opacity: 1, color: "#159895" }, transition: "all 0.2s" }}
                  >
                    {text}
                  </Typography>
                </a>
              ))}
            </div>
          </motion.div>
        </Grid>
      </Grid>

      {/* ── Copyright bar ─────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div
          style={{
            height: 1,
            background: "linear-gradient(90deg, transparent, rgba(21,152,149,0.2), transparent)",
            marginBottom: 20,
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingInline: "clamp(24px, 8vw, 64px)",
            paddingBottom: 28,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Typography fontFamily="Poppins" fontSize={11} sx={{ opacity: 0.35, letterSpacing: "0.04em" }}>
            © {new Date().getFullYear()} Utsav Patel. All rights reserved.
          </Typography>
          <Typography fontFamily="Poppins" fontSize={11} sx={{ opacity: 0.35, letterSpacing: "0.04em" }}>
            Crafted with{" "}
            <span style={{ color: "#159895" }}>♥</span>
            {" "}using React & GSAP
          </Typography>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
